import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, OnDestroy, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { Currency } from '@galaxy/billing';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CountryCode } from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { NgForOf, NgIf } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

interface CountryOption {
  value: CountryCodes;
  label: string;
}

interface CurrencyOption {
  value: Currency;
  label: string;
}

export interface CreateBankAccountRequest {
  country: string;
  currency: string;
  routing_number?: string;
  account_number: string;
}

enum CountryCodes {
  US = 'US',
  CA = 'CA',
  CZ = 'CZ',
  AU = 'AU',
  NZ = 'NZ',
  GB = 'GB',
  AE = 'AE',
  DE = 'DE',
}

@Component({
  selector: 'smb-invoicing-add-bank-account-form',
  templateUrl: './add-bank-account.component.html',
  styleUrls: ['./add-bank-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatSelectModule,
    NgForOf,
    NgIf,
    MatInput,
    TranslateModule,
  ],
  providers: [TranslateService],
})
export class AddBankAccountComponent implements OnInit, OnDestroy {
  merchantCountry = input<string>();
  addBankAccountError = input<string>();

  private subscriptions: Subscription[] = [];

  public countryForm: UntypedFormGroup;
  public unitedStatesForm: UntypedFormGroup;
  public canadaForm: UntypedFormGroup;
  public czechRepublicForm: UntypedFormGroup;
  public australiaForm: UntypedFormGroup;
  public newZealandForm: UntypedFormGroup;
  public unitedKingdomForm: UntypedFormGroup;
  public unitedArabEmiratesForm: UntypedFormGroup;
  public germanyForm: UntypedFormGroup;

  private countryOptionUS: CountryOption;
  private countryOptionCA: CountryOption;
  private countryOptionCZ: CountryOption;
  private countryOptionAU: CountryOption;
  private countryOptionNZ: CountryOption;
  private countryOptionGB: CountryOption;
  private countryOptionAE: CountryOption;
  private countryOptionDE: CountryOption;

  public currencyOptions: CurrencyOption[] = [];
  public countryOptions: CountryOption[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.countryOptionUS = {
      value: CountryCodes.US,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.UNITED_STATES'),
    };
    this.countryOptionCA = {
      value: CountryCodes.CA,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.CANADA'),
    };
    this.countryOptionCZ = {
      value: CountryCodes.CZ,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.CZECH_REPUBLIC'),
    };
    this.countryOptionAU = {
      value: CountryCodes.AU,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.AUSTRALIA'),
    };
    this.countryOptionNZ = {
      value: CountryCodes.NZ,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.NEW_ZEALAND'),
    };
    this.countryOptionGB = {
      value: CountryCodes.GB,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.UNITED_KINGDOM'),
    };
    this.countryOptionAE = {
      value: CountryCodes.AE,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.UNITED_ARAB_EMIRATES'),
    };
    this.countryOptionDE = {
      value: CountryCodes.DE,
      label: this.translateService.instant('BANK_ACCOUNT.ADD.COUNTRY.GERMANY'),
    };

    this.currencyOptions = [
      { value: Currency.CAD, label: this.translateService.instant('BANK_ACCOUNT.ADD.CURRENCY_OPTION.CAD') },
      { value: Currency.USD, label: this.translateService.instant('BANK_ACCOUNT.ADD.CURRENCY_OPTION.USD') },
    ];

    this.countryForm = this.formBuilder.group({
      country: ['', Validators.required],
      currency: ['', Validators.required],
    });

    this.unitedStatesForm = this.formBuilder.group({
      routingNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{9}`)]],
      accountNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{7,17}`)]],
    });
    this.canadaForm = this.formBuilder.group({
      transitNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{5}`)]],
      institutionNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{3}`)]],
      accountNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{7,17}`)]],
    });
    this.czechRepublicForm = this.formBuilder.group({
      iban: ['', [Validators.required, Validators.pattern(`^[A-Za-z0-9]{24}`)]],
    });
    this.australiaForm = this.formBuilder.group({
      bsb: ['', [Validators.required, Validators.pattern(`^[0-9]{6}`)]],
      accountNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{5,9}`)]],
    });
    this.newZealandForm = this.formBuilder.group({
      accountNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{15,16}`)]],
    });
    this.unitedKingdomForm = this.formBuilder.group({
      sortCode: ['', [Validators.required, Validators.pattern(`(^[0-9]{2}[-]{1}[0-9]{2}[-]{1}[0-9]{2})|(^[0-9]{6})`)]],
      account: ['', [Validators.required, Validators.pattern(`^[0-9]{8}`)]],
    });
    this.unitedArabEmiratesForm = this.formBuilder.group({
      iban: ['', [Validators.required, Validators.pattern(`^[A-Za-z0-9]{23}`)]],
    });
    this.germanyForm = this.formBuilder.group({
      iban: ['', [Validators.required, Validators.pattern(`^[A-Za-z0-9]{22}`)]],
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.country.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap((country) => {
            this.currency.disable();
            switch (country) {
              case CountryCodes.CA:
                this.currency.patchValue(Currency.CAD);
                this.currency.enable();
                break;
              case CountryCodes.US:
                this.currency.patchValue(Currency.USD);
                break;
              case CountryCodes.CZ:
                this.currency.patchValue(Currency.CZK);
                break;
              case CountryCodes.AU:
                this.currency.patchValue(Currency.AUD);
                break;
              case CountryCodes.NZ:
                this.currency.patchValue(Currency.NZD);
                break;
              case CountryCodes.GB:
                this.currency.patchValue(Currency.GBP);
                break;
              case CountryCodes.AE:
                this.currency.patchValue('AED');
                break;
              case CountryCodes.DE:
                this.currency.patchValue(Currency.EUR);
                break;
            }
          }),
        )
        .subscribe(),
    );
    const countryCode = this.merchantCountry() ? CountryCodes[this.merchantCountry() as CountryCodes] : CountryCodes.US;
    switch (countryCode) {
      case CountryCodes.CA:
        this.countryOptions = [this.countryOptionCA, this.countryOptionUS];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.US:
        this.countryOptions = [this.countryOptionUS];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.CZ:
        this.countryOptions = [this.countryOptionCZ, this.countryOptionGB];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.AU:
        this.countryOptions = [this.countryOptionAU];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.NZ:
        this.countryOptions = [this.countryOptionNZ];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.GB:
        this.countryOptions = [this.countryOptionGB, this.countryOptionUS];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.AE:
        this.countryOptions = [this.countryOptionAE];
        this.country.patchValue(countryCode);
        break;
      case CountryCodes.DE:
        this.countryOptions = [this.countryOptionDE];
        this.country.patchValue(countryCode);
        break;
      default:
        /*Default to all options, and select US. We shouldn't necessarily ever get to this state, as this dialog
        should only be accessible if you are in a supported country.
        if an invalid country is selected by the user, stripe will return an error upon form submission */
        this.countryOptions = [
          this.countryOptionCA,
          this.countryOptionUS,
          this.countryOptionCZ,
          this.countryOptionAU,
          this.countryOptionNZ,
          this.countryOptionGB,
          this.countryOptionAE,
          this.countryOptionDE,
        ];
        this.country.patchValue(CountryCodes.US);
        break;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get country(): UntypedFormControl {
    return this.countryForm.get('country') as UntypedFormControl;
  }

  get currency(): UntypedFormControl {
    return this.countryForm.get('currency') as UntypedFormControl;
  }

  buildUSAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      routing_number: this.unitedStatesForm.get('routingNumber').value,
      account_number: this.unitedStatesForm.get('accountNumber').value,
    };
  }

  buildCanadianAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      routing_number: `${this.canadaForm.get('transitNumber').value}${this.canadaForm.get('institutionNumber').value}`,
      account_number: this.canadaForm.get('accountNumber').value,
    };
  }

  buildCzechAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      account_number: this.czechRepublicForm.get('iban').value,
    };
  }
  buildAustralianAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      routing_number: this.australiaForm.get('bsb').value,
      account_number: this.australiaForm.get('accountNumber').value,
    };
  }

  buildNewZealandAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      account_number: this.newZealandForm.get('accountNumber').value,
    };
  }

  buildUnitedKingdomAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      routing_number: this.unitedKingdomForm.get('sortCode').value.replaceAll('-', ''),
      account_number: this.unitedKingdomForm.get('account').value,
    };
  }

  buildUnitedArabEmiratesAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      account_number: this.unitedArabEmiratesForm.get('iban').value,
    };
  }

  buildGermanyAccountRequest(): CreateBankAccountRequest {
    return {
      country: this.country.value,
      currency: this.currency.value.toLowerCase(),
      account_number: this.germanyForm.get('iban').value,
    };
  }

  getFormByCountry(country: CountryCode): UntypedFormGroup {
    switch (country) {
      case CountryCodes.US:
        return this.unitedStatesForm;
      case CountryCodes.CA:
        return this.canadaForm;
      case CountryCodes.CZ:
        return this.czechRepublicForm;
      case CountryCodes.AU:
        return this.australiaForm;
      case CountryCodes.NZ:
        return this.newZealandForm;
      case CountryCodes.GB:
        return this.unitedKingdomForm;
      case CountryCodes.AE:
        return this.unitedArabEmiratesForm;
      case CountryCodes.DE:
        return this.germanyForm;
    }
  }

  validate(): CreateBankAccountRequest | null {
    const countryForm = this.getFormByCountry(this.country.value);
    countryForm.markAllAsTouched();
    countryForm.markAsDirty();
    this.cdr.markForCheck();
    if (!countryForm.valid) {
      return null;
    }
    switch (this.country.value) {
      case CountryCodes.US:
        return this.buildUSAccountRequest();
      case CountryCodes.CA:
        return this.buildCanadianAccountRequest();
      case CountryCodes.CZ:
        return this.buildCzechAccountRequest();
      case CountryCodes.AU:
        return this.buildAustralianAccountRequest();
      case CountryCodes.NZ:
        return this.buildNewZealandAccountRequest();
      case CountryCodes.GB:
        return this.buildUnitedKingdomAccountRequest();
      case CountryCodes.AE:
        return this.buildUnitedArabEmiratesAccountRequest();
      case CountryCodes.DE:
        return this.buildGermanyAccountRequest();
    }
    return null;
  }
}
